<template>
  <div>
    <StateRenderer v-bind="rents">
      <b-alert
        v-if="Array.isArray(rents.data) && rents.data.length === 0"
        show
        variant="secondary"
        class="text-center"
      >
        Nenhum aluguel realizado neste período
      </b-alert>

      <template v-else>
        <h4>
          <b>Total: {{ calculateTotal | formatCurrency }}</b>
          <b-icon-info-circle
            v-b-tooltip.hover
            title="Valor total de movimentação entre as datas selecionadas!"
            font-scale="0.6"
            class="ml-1 align-super"
            style="vertical-align: super"
          />
        </h4>

        <!-- BOTÕES DE PAGINAÇÃO -->
        <div class="pagination-container mt-3">
          <b-button
            variant="primary"
            :disabled="rents.page <= 1"
            @click="prevPage"
          >
            Anterior
          </b-button>

          <span class="page-info">
            Página {{ rents.data.page }} de {{ rents.data.totalPages }}
          </span>

          <b-button
            variant="primary"
            :disabled="rents.page >= rents.totalPages"
            @click="nextPage"
          >
            Próxima
          </b-button>
        </div>
        <!-- FIM PAGINAÇÃO -->

        <!-- Tabela -->
        <div class="table-frame">
          <b-table
            ref="rentsList"
            :fields="fields"
            :items="rents.data.data"
            striped
            hover
            small
            :busy="rents.loading"
            sticky-header="80vh"
            bordered
            head-variant="light"
            :tbody-tr-class="rowClass"
          >
            <template #table-colgroup="scope">
              <col v-for="field in scope.fields" :key="field.key" />
            </template>

            <!-- Coluna Cliente com ícone -->
            <template #cell(customer.name)="{ item }">
              <div class="d-flex align-items-center">
                <span class="mr-2">
                  {{
                    item.customer && item.customer.name
                      ? item.customer.name
                      : '—'
                  }}
                </span>
                <b-button
                  v-if="item.customer"
                  v-b-tooltip.hover
                  size="sm"
                  variant="link"
                  class="p-0 customer-info-btn"
                  title="Ver informações do cliente"
                  @click="openCustomerModal(item.customer)"
                >
                  <b-icon-info-circle class="align-text-top" font-scale="1.1" />
                </b-button>
              </div>
            </template>

            <template #cell(period.time)="{ item }">
              <span class="d-block">
                {{ item.period ? item.period.time : 'Período não disponível' }}
                <b-icon-info-circle
                  v-b-tooltip.hover
                  :title="
                    item.period && item.period.price
                      ? `Valor total do período: ${item.period.price}`
                      : 'Período não disponível'
                  "
                  font-scale="0.6"
                  class="ml-1 align-super"
                  style="vertical-align: super"
                />
              </span>
            </template>

            <template #cell(createdAt)="{ item }">
              <span class="d-block">{{ getDate(item.createdAt) }}</span>
            </template>

            <template #cell(startsAt)="{ item }">
              <span class="d-block">{{ item.createdAt | formatHours }}</span>
            </template>

            <template #cell(endsAt)="{ item }">
              <span class="d-block">
                {{ calculateEndDate(item) | formatHours }}
              </span>
            </template>

            <template #cell(extraTime)="{ item }">
              <span class="d-block">{{ getExtraTime(item) }}</span>
            </template>

            <template #cell(pausedTime)="{ item }">
              <span class="d-block">{{ getPausedTime(item) }}</span>
            </template>

            <template #cell(exceededTime)="{ item }">
              <span class="d-block">{{ getExceededTime(item) }}</span>
            </template>

            <template #cell(rentTime)="{ item }">
              <span class="d-block">{{
                getRentTime(item) - getPausedTime(item)
              }}</span>
            </template>

            <template #cell(timeCharged)="{ item }">
              <span class="d-block">
                {{
                  getRentTime(item) - getPausedTime(item) - getExtraTime(item)
                }}
              </span>
            </template>

            <template #cell(extraTimeReason)="{ item }">
              <span class="d-block">{{ getExtraTimeReason(item) }}</span>
            </template>

            <template #cell(cancelReason)="{ item }">
              <span class="d-block">
                {{
                  item.cancelRent && item.cancelRent.reason
                    ? item.cancelRent.reason
                    : '—'
                }}
              </span>
            </template>

            <template #cell(products)="{ item }">
              <span class="d-block">{{ item.products.length }}</span>
            </template>

            <template #cell(cash)="{ item }">
              <span class="d-block">
                {{ calculateReceivables(item, 'cash') | formatCurrency }}
              </span>
            </template>

            <template #cell(debitCard)="{ item }">
              <span class="d-block">
                {{ calculateReceivables(item, 'debitCard') | formatCurrency }}
              </span>
            </template>

            <template #cell(creditCard)="{ item }">
              <span class="d-block">
                {{ calculateReceivables(item, 'creditCard') | formatCurrency }}
              </span>
            </template>

            <template #cell(pix)="{ item }">
              <span class="d-block">
                {{ calculateReceivables(item, 'pix') | formatCurrency }}
              </span>
            </template>

            <template #cell(status)="{ item }">
              <Status :value="getStatus(item)" />
            </template>
          </b-table>
        </div>
        <!-- Modal de informações do cliente -->
        <CustomerInfoModal
          v-if="selectedCustomer"
          :customer="selectedCustomer"
          :visible="showCustomerModal"
          @close="closeCustomerModal"
        />
      </template>
    </StateRenderer>
  </div>
</template>

<script>
import { BIconInfoCircle } from 'bootstrap-vue'
import { RENT_STATUS } from '@/constants'
import { reduce, pipe, filter } from 'lodash/fp'
import { formatCurrency } from '@/helpers/numbers'
import { formatDate, formatDateTime, formatHours } from '@/helpers/date'
import { format, addMinutes, differenceInMinutes } from 'date-fns'
import { calculateExtraTime, calculatePausedTime } from '@/helpers/rents'
import StateRenderer from '@/components/StateRenderer'
import ptBRLocale from 'date-fns/locale/pt-BR'
import periods from '@/services/modules/periods'
import Status from '@/components/Status'
import CustomerInfoModal from '@/components/Home/partials/CustomerInfoModal.vue'

export default {
  name: 'RentsReportList',
  filters: { formatCurrency, formatDate, formatHours, formatDateTime },
  components: {
    Status,
    StateRenderer,
    BIconInfoCircle,
    CustomerInfoModal
  },
  props: {
    rents: { type: Object, required: true },
    paymentsValue: { type: Array, required: true }
  },
  data() {
    return {
      refreshComponent: 0,
      showCustomerModal: false,
      selectedCustomer: null
    }
  },
  computed: {
    fields() {
      return [
        { key: 'createdAt', label: 'Data' },
        { key: 'toy.name', label: 'Brinquedo' },
        { key: 'customer.name', label: 'Cliente' },
        { key: 'startsAt', label: 'Início' },
        { key: 'endsAt', label: 'Retorno' },
        { key: 'period.time', label: 'Período' },
        { key: 'extraTime', label: 'Adicional' },
        { key: 'pausedTime', label: 'Pausado' },
        { key: 'exceededTime', label: 'Excedido' },
        { key: 'rentTime', label: 'Permanência' },
        { key: 'timeCharged', label: 'Cobrado' },
        { key: 'extraTimeReason', label: 'Motivo tempo adicional' },
        { key: 'cancelReason', label: 'Motivo do Cancelamento' },
        { key: 'cash', label: 'Total Dinheiro' },
        { key: 'creditCard', label: 'Total Crédito' },
        { key: 'debitCard', label: 'Total Débito' },
        { key: 'pix', label: 'Total Pix' },
        { key: 'status', label: 'Status' }
      ]
    },
    calculateTotal() {
      if (!Array.isArray(this.paymentsValue)) return 0
      return this.paymentsValue.reduce(
        (total, payment) => total + (payment.amount || 0),
        0
      )
    }
  },
  watch: {
    'rents.data': {
      handler() {
        this.recoverDeletedPeriods()
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    this.timer = setInterval(this.updateComponent, 50000)
    this.recoverDeletedPeriods()
  },
  beforeDestroy() {
    clearInterval(this.timer)
  },
  methods: {
    nextPage() {
      if (this.rents.data.page < this.rents.data.totalPages) {
        this.$emit('change-page', this.rents.data.page + 1)
      }
    },
    prevPage() {
      if (this.rents.data.page > 1) {
        this.$emit('change-page', this.rents.data.page - 1)
      }
    },
    updateComponent() {
      this.refreshComponent++
      if (Array.isArray(this.rents.data)) {
        this.rents.data.forEach((rent) => {
          if (rent.status === RENT_STATUS.PAUSED.value) {
            rent.pausedTime = this.getPausedTime(rent)
          }
        })
      }
    },
    rowClass(item) {
      return item.status === 'canceled' ? 'table-danger' : 'table-light'
    },
    getDate(date) {
      return format(new Date(date), 'dd/MM/yyyy', { locale: ptBRLocale })
    },
    calculateEndDate(rent) {
      if (!rent.period) return 'Período Incorreto'
      const rentTime = this.getRentTime(rent)
      const startDate = new Date(rent.createdAt)
      return rentTime <= rent.period.time
        ? addMinutes(startDate, rent.period.time)
        : addMinutes(startDate, rentTime)
    },
    calculateReceivables(rent, type) {
      return pipe(
        filter((item) => item.type === type),
        reduce((result, current) => (result += current.amount), 0)
      )(rent.payments)
    },

    /**
     * Tempo pausado:
     * - Somatório de pausas fechadas (calculatePausedTime)
     * - Se estiver pausado em aberto: pausedAt -> now
     * - Se finalizar/cancelar ainda pausado: pausedAt -> endedAt|canceledAt
     */
    getPausedTime(rent) {
      const basePaused = calculatePausedTime(rent) || 0

      if (!rent.pausedAt) return basePaused

      const pausedAt = new Date(rent.pausedAt)
      const resumedAt = rent.resumedAt ? new Date(rent.resumedAt) : null
      const pauseStillOpenSincePausedAt = !resumedAt || resumedAt <= pausedAt

      // 1) Pausado em aberto (sem end/cancel) -> até agora
      if (
        rent.status === RENT_STATUS.PAUSED.value &&
        !rent.endedAt &&
        !rent.canceledAt
      ) {
        const now = new Date()
        return basePaused + Math.max(0, differenceInMinutes(now, pausedAt))
      }

      // 2) Finalizado enquanto pausado -> pausedAt -> endedAt
      if (rent.endedAt && pauseStillOpenSincePausedAt) {
        const endedAt = new Date(rent.endedAt)
        if (endedAt >= pausedAt) {
          return (
            basePaused + Math.max(0, differenceInMinutes(endedAt, pausedAt))
          )
        }
      }

      // 3) Cancelado enquanto pausado -> pausedAt -> canceledAt
      if (rent.canceledAt && pauseStillOpenSincePausedAt) {
        const canceledAt = new Date(rent.canceledAt)
        if (canceledAt >= pausedAt) {
          return (
            basePaused + Math.max(0, differenceInMinutes(canceledAt, pausedAt))
          )
        }
      }

      // Demais casos: apenas o acumulado
      return basePaused
    },

    getExtraTime(rent) {
      return calculateExtraTime(rent) || 0
    },

    getExtraTimeReason(rent) {
      const list = Array.isArray(rent.extraTime) ? rent.extraTime : []
      if (!list.length) return '—'
      return reduce(
        (result, current) =>
          result + (result === '' ? '' : ' / ') + (current.reason || '—'),
        '',
        list
      )
    },

    getRentTime(rent) {
      let endDate
      if (rent.canceledAt) {
        endDate = new Date(rent.canceledAt)
      } else if (rent.endedAt) {
        endDate = new Date(rent.endedAt)
      } else {
        endDate = new Date()
      }
      const startDate = new Date(rent.createdAt)
      return Math.max(0, differenceInMinutes(endDate, startDate))
    },

    /**
     * Excedido = (tempo total) - (pausado) - (adicional) - (tempo do período)
     * Nunca negativo.
     */
    getExceededTime(rent) {
      if (!rent.period) return 'Período Incorreto'
      const netTime =
        this.getRentTime(rent) -
        this.getPausedTime(rent) -
        this.getExtraTime(rent)

      if (netTime <= rent.period.time) return 0
      return Math.max(0, netTime - rent.period.time)
    },

    getStatus(rent) {
      if (rent.status === 'rescued') return RENT_STATUS.RESCUED.value
      if (rent.canceledAt) return RENT_STATUS.CANCELLED.value
      if (rent.status === 'active') return RENT_STATUS.ACTIVE.value
      if (rent.endedAt) return RENT_STATUS.ENDED.value
      if (rent.pausedAt) return RENT_STATUS.PAUSED.value
      return RENT_STATUS.ACTIVE.value
    },

    openCustomerModal(customer) {
      this.selectedCustomer = customer
      this.showCustomerModal = true
    },
    closeCustomerModal() {
      this.showCustomerModal = false
      this.selectedCustomer = null
    },

    async recoverDeletedPeriods() {
      if (!this.rents.data || !Array.isArray(this.rents.data)) return
      const rents = this.rents.data
      const missingPeriodRents = rents.filter(
        (rent) => !rent.period && rent.periodId
      )
      const periodIds = [
        ...new Set(missingPeriodRents.map((rent) => rent.periodId))
      ]
      const periodPromises = periodIds.map((periodId) =>
        periods.fetchPeriodById(periodId)
      )
      const periodsFetched = await Promise.all(periodPromises)
      const periodsById = {}
      periodsFetched.forEach((period) => {
        periodsById[period.id] = period
      })
      missingPeriodRents.forEach((rent) => {
        if (periodsById[rent.periodId]) {
          rent.period = periodsById[rent.periodId]
        }
      })
    }
  }
}
</script>

<style scoped>
.table-frame {
  border: 1px solid #dee2e6; /* mesma cor padrão do Bootstrap */
  border-radius: 0.25rem;
  overflow: hidden; /* recorta os cantos arredondados do header sticky */
}
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.page-info {
  text-align: center;
  flex-grow: 1;
  font-size: 1rem;
  font-weight: bold;
}
.customer-info-btn :deep(svg) {
  vertical-align: text-top;
}
</style>
